import PropTypes from 'prop-types';
import MomentPropTypes from 'react-moment-proptypes';
import { orderCompanyPropTypes } from './company.type';
import { stopsPropTypes } from './stop.type';

export const OrderContext = {
  IN: 'in',
  OUT: 'out',
  UNKNOWN: 'unknown',
};

export const OrderType = {
  TIME: 'time',
  TOUR: 'tour',
  UNKNOWN: 'unknown',
};

export const EditionMode = {
  SHIPPER: 'SHIPPER',
  CARRIER: 'CARRIER',
  UNKNOWN: 'UNKNOWN',
};

export const orderPropTypes = PropTypes.shape({
  id: PropTypes.string,
  _type: PropTypes.oneOf(Object.values(OrderContext)),
  name: PropTypes.string,
  vehicles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    licencePlateNumber: PropTypes.string,
    licencePlateCountryCode: PropTypes.string,
    trackingTimeframe: PropTypes.shape({
      periods: PropTypes.arrayOf(PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
      })),
    }),
  })),
  internalId: PropTypes.string,
  shipperCompany: orderCompanyPropTypes,
  carrierCompany: orderCompanyPropTypes,
  personId: PropTypes.string,
  startDate: PropTypes.number,
  endDate: PropTypes.number,
  createdDate: PropTypes.number,
  active: PropTypes.bool,
  statusDate: PropTypes.number,
  system: PropTypes.string,
  user: PropTypes.string,
  stops: stopsPropTypes,
  type: PropTypes.string,
  actualTravelledDistanceInMeters: PropTypes.number,
  creationProblems: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
    fieldPath: PropTypes.string,
    value: PropTypes.string,
  })),
});

export const ordersResponsePropTypes = PropTypes.shape({
  loading: PropTypes.bool.isRequired,
  majorReload: PropTypes.bool.isRequired,
  lastUpdated: MomentPropTypes.momentObj,
  data: PropTypes.shape({
    totalElements: PropTypes.number,
    tours: PropTypes.arrayOf(orderPropTypes),
  }),
});

const etaDetailsPropTypes = PropTypes.shape({
  state: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  remainingDriveTimeCapacity: PropTypes.shape({
    current: PropTypes.number,
    daily: PropTypes.number,
    weekly: PropTypes.number,
  }).isRequired,
  totalTime: PropTypes.number.isRequired,
  itinerary: PropTypes.arrayOf(PropTypes.shape({
    activity: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
  })),
});

export const estimatedEtaPropTypes = PropTypes.shape({
  vehicleId: PropTypes.string.isRequired,
  licensePlateNumber: PropTypes.string.isRequired,
  eta: PropTypes.shape({
    arrival: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    details: etaDetailsPropTypes,
  }).isRequired,
  path: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  })),
});

export const geofencingAssetPropTypes = PropTypes.shape({
  assetId: PropTypes.string.isRequired,
  licensePlateNumber: PropTypes.string.isRequired,
  in: PropTypes.number,
  out: PropTypes.number,
});

export const geofencingStopPropTypes = PropTypes.shape({
  stopId: PropTypes.string.isRequired,
  assets: PropTypes.arrayOf(
    geofencingAssetPropTypes,
  ),
});

export const geofencingDataPropTypes = PropTypes.shape({
  stops: PropTypes.arrayOf(
    geofencingStopPropTypes,
  ),
});

export const monitoringOrderStopAssetSnapshotPropTypes = PropTypes.shape({
  assetId: PropTypes.string.isRequired,
  licensePlateNumber: PropTypes.string.isRequired,
  arrivalTime: PropTypes.number,
  departureTime: PropTypes.number,
  arrivalStatus: PropTypes.string,
  departureStatus: PropTypes.string,
});

export const monitoringOrderStopSnapshotPropTypes = PropTypes.shape({
  stopId: PropTypes.string.isRequired,
  stopSnapshots: PropTypes.arrayOf(
    monitoringOrderStopAssetSnapshotPropTypes,
  ),
});

export const monitoringOrderSnapshotPropTypes = PropTypes.shape({
  stopSnapshots: PropTypes.arrayOf(
    monitoringOrderStopSnapshotPropTypes,
  ),
});

export const etaPropTypes = PropTypes.objectOf(PropTypes.oneOfType([
  PropTypes.shape({
    estimated: estimatedEtaPropTypes,
    unestimated: PropTypes.objectOf(PropTypes.shape({
      vehicleId: PropTypes.string.isRequired,
      reasonCode: PropTypes.string.isRequired,
    })),
  }),
  PropTypes.bool,
  PropTypes.string,
]));

export const orderTypePropTypes = PropTypes.oneOf(Object.values(OrderContext));
